import { type PartnerReference } from '@capsicum/common';
import { useMainCurrenciesQuery } from '@capsicum/socios-core';
import { useCallback } from 'react';

import { type NCWalletTokenCardsBalanceEntry } from '../models';

export const useEmptyNCWalletTokenBalanceCreator = () => {
	const { data: currencies, isLoading: isCurrencyLoading } = useMainCurrenciesQuery();

	const altShortCode = currencies?.altCurrency?.shortCode ?? 'EUR';
	const defaultShortCode = currencies?.defaultCurrency?.shortCode ?? 'CHZ';

	const create = useCallback<
		(options: {
			isTopMovingToken?: boolean;
			partner: PartnerReference;
		}) => NCWalletTokenCardsBalanceEntry | undefined
	>(
		({ isTopMovingToken = false, partner }) => {
			if (!partner.id) return;

			// Although the endpoint returns an array of Markets, a partner will always be linked to 1 Market.
			const baseCurrency = partner.markets?.[0]?.baseCurrency;

			if (!baseCurrency) return;

			const { id: currencyId, shortCode } = baseCurrency;

			if (!shortCode || !currencyId) return;

			const logo = partner.images?.find(image => image.imageType?.type === 'partner_logo');

			return {
				amount: 0,
				currencyId,
				formattedAltBalance: `0 ${altShortCode}`,
				formattedDefaultBalance: `0 ${defaultShortCode}`,
				isTopMovingToken,
				partnerID: partner.id,
				shortCode,
				title: baseCurrency.label ?? '',
				tokenBalance: `0 ${shortCode}`,
				tokenImgBlurhash: logo?.blurHash,
				tokenImgUrl: logo?.url,
				tokenType: 'partner',
				type: 'balance',
				usableFormattedAltBalance: `0 ${altShortCode}`,
				usableFormattedDefaultBalance: `0 ${defaultShortCode}`,
				usableTokenBalance: `0 ${shortCode}`
			};
		},
		[altShortCode, defaultShortCode]
	);

	return {
		create,
		isLoading: isCurrencyLoading
	};
};
